import { useEffect, useState } from "react";
import { Box, Button, Flex, IconButton, Input, InputGroup, InputLeftElement, ModalBody, ModalCloseButton, ModalContent, ModalFooter, ModalHeader, ModalOverlay, Modal, Spinner, Text } from "@chakra-ui/react";
import { SearchIcon, CloseIcon } from "@chakra-ui/icons";
import { CustomToast } from "../../../../components";
import { useFetch } from "../../../../hooks/useFetch";

interface Candidate { employee_Id: number; employee_Name: string; employee_Code: string; }

interface MoveMembersModalProps {
    isOpen: boolean;
    onClose: () => void;
    memberIds: number[];
    currentLeaderId?: number;
    roleType?: string;
    branchIds?: number[];
    onMoved?: () => void;
}

const MoveMembersModal = ({ isOpen, onClose, memberIds, currentLeaderId, roleType, branchIds, onMoved }: MoveMembersModalProps) => {
    const { addToast } = CustomToast();
    const { fetchApi } = useFetch(addToast);

    const [query, setQuery] = useState('');
    const [debounced, setDebounced] = useState('');
    const [results, setResults] = useState<Candidate[]>([]);
    const [loading, setLoading] = useState(false);
    const [selected, setSelected] = useState<Candidate | null>(null);
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        setQuery('');
        setResults([]);
        setSelected(null);
    }, [isOpen]);

    useEffect(() => {
        const t = setTimeout(() => setDebounced(query), 400);
        return () => clearTimeout(t);
    }, [query]);

    useEffect(() => {
        if (!debounced.trim()) { setResults([]); return; }
        (async () => {
            setLoading(true);

            const params = new URLSearchParams();
            params.append('filter', debounced);
            if (currentLeaderId) params.append('excludeEmployeeId', String(currentLeaderId));
            (branchIds || []).forEach(id => params.append('branchIds', String(id)));

            const res = await fetchApi<Candidate[]>('TeamInteraction/teamLeadManagerSearch', 'GET', '', params.toString());
            setResults(res || []);
            setLoading(false);
        })();
    }, [debounced, branchIds, currentLeaderId]);

    const handleMove = async () => {
        if (!selected) { addToast({ message: 'Select the new TL/Manager', status: 'error' }); return; }
        setSaving(true);
        try {
            const res = await fetchApi('TeamInteraction/moveMembers', 'POST', {
                member_Employee_Ids: memberIds,
                new_Leader_Id: selected.employee_Id,
                role_Type: roleType,
            });
            if (res) {
                addToast({ message: 'Members moved successfully', status: 'success' });
                onMoved?.();
            }
        } finally {
            setSaving(false);
        }
    };

    const multiBranch = (branchIds?.length ?? 0) > 1;
    const noEligibleCandidates = !!debounced.trim() && !loading && results.length === 0;

    return (
        <Modal isOpen={isOpen} onClose={onClose} isCentered size="md">
            <ModalOverlay />
            <ModalContent borderRadius="12px">
                <ModalHeader fontSize="15px" fontWeight="700">Move {memberIds.length} member(s)</ModalHeader>
                <ModalCloseButton />
                <ModalBody>
                    <Text fontSize="12px" color="#64748B" mb={3}>
                        Pick the new {roleType === 'Manager' ? 'Manager' : 'Team Lead'} to move these members to.
                    </Text>

                    {multiBranch && (
                        <Text fontSize="11px" color="#B45309" bg="#FFFBEB" border="1px solid #FDE68A" borderRadius="6px" px={2} py={1.5} mb={3}>
                            Selected members span {branchIds!.length} different branches — only Team Leads/Managers with access to all of them are shown.
                        </Text>
                    )}

                    {selected ? (
                        <Flex align="center" gap={2} bg="#F0FDF4" border="1px solid #BBF7D0" borderRadius="8px" px={3} py={2}>
                            <Box w="7px" h="7px" borderRadius="full" bg="#16A34A" />
                            <Text fontSize="13px" fontWeight="600" color="#15803D" flex={1}>{selected.employee_Name}</Text>
                            <IconButton aria-label="Clear" icon={<CloseIcon boxSize={2} />} size="xs" variant="ghost" onClick={() => setSelected(null)} />
                        </Flex>
                    ) : (
                        <Box position="relative">
                            <InputGroup size="sm">
                                <InputLeftElement pointerEvents="none" h="36px"><SearchIcon color="gray.400" boxSize={3} /></InputLeftElement>
                                <Input h="36px" pl={8} placeholder="Search TL / Manager…" value={query} onChange={e => setQuery(e.target.value)} />
                                {loading && <Spinner size="xs" position="absolute" right={3} top="10px" />}
                            </InputGroup>

                            {noEligibleCandidates && (
                                <Text fontSize="11px" color="#DC2626" mt={1.5}>
                                    No one has access to all the required branches.
                                </Text>
                            )}

                            {results.length > 0 && (
                                <Box position="absolute" top="100%" left={0} right={0} zIndex={20} bg="white" border="1px solid #E2E8F0" borderRadius="8px" boxShadow="0 4px 16px rgba(0,0,0,0.10)" maxH="200px" overflowY="auto" mt={1}>
                                    {results.map(c => (
                                        <Flex key={c.employee_Id} align="center" gap={2} px={3} py={2} cursor="pointer" _hover={{ bg: '#F0FDF4' }} onClick={() => { setSelected(c); setQuery(''); setResults([]); }}>
                                            <Text fontSize="13px" fontWeight="500">{c.employee_Name}</Text>
                                            <Text fontSize="11px" color="#94A3B8" fontFamily="monospace">{c.employee_Code}</Text>
                                        </Flex>
                                    ))}
                                </Box>
                            )}
                        </Box>
                    )}
                </ModalBody>
                <ModalFooter gap={3}>
                    <Button variant="ghost" onClick={onClose} isDisabled={saving}>Cancel</Button>
                    <Button bg="#006838" color="white" _hover={{ bg: '#005530' }} isLoading={saving} onClick={handleMove}>Move</Button>
                </ModalFooter>
            </ModalContent>
        </Modal>
    );
};

export default MoveMembersModal;